'use client'

import React from 'react';

/**
 * DisclaimerSection Component
 * Organizes legal text into clean, digestible blocks
 */
const DisclaimerSection = ({ title, children, isStandard = false }) => {
  return (
    <div className={`w-full mb-10 p-6 md:p-10 rounded-3xl border transition-all duration-300 ${
      isStandard 
      ? "bg-indigo-500/5 border-indigo-500/20 shadow-lg shadow-indigo-500/5"
      : "bg-gray-50 border-gray-200"
    }`}>
      {title && (
        <h2 className={`text-xl md:text-2xl font-bold mb-6 tracking-tight ${
          isStandard ? "text-indigo-700" : "text-gray-900"
        }`}>
          {title}
        </h2>
      )}
      <div className="space-y-6 text-gray-700 leading-relaxed text-base md:text-lg text-justify md:text-left">
        {children}
      </div>
    </div>
  );
};

export default function LegalisclDaimer() {
  return (
    <div className="min-h-screen bg-white text-gray-700 selection:bg-indigo-500/20">
      
      {/* Background Decorative Blurs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[50%] h-[50%] rounded-full bg-blue-900/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[40%] h-[40%] rounded-full bg-indigo-900/15 blur-[100px]" />
      </div>

      <main className="relative z-10 w-full px-4 sm:px-8 md:px-16 lg:px-24 py-16">
        
        {/* Header Section */}
        <header className="mb-16 border-b border-gray-100 pb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full border border-[#1e6091]/30 bg-[#1e6091]/10 text-[#1e6091] text-xs font-bold uppercase tracking-widest">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1e6091] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1e6091]"></span>
            </span>
            Legal Protection
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 tracking-tighter mb-6">
            Disclaimer & Terms
          </h1>
          <p className="text-gray-600 text-lg max-w-4xl leading-relaxed">
            Please read these terms carefully. By using <span className="text-gray-900 font-semibold">Option Bricks</span>, 
            you acknowledge that you have read, understood, and agreed to be bound by the following legal conditions.
          </p>
        </header>

        {/* Content Flow */}
        <div className="flex flex-col w-full">
          
          <DisclaimerSection>
            <p>
              Option Bricks is not responsible for the information and conclusions of the analysis available on it. 
              Although all the information on the website is presented with utmost care, we do not hold any 
              responsibility for the information provided.
            </p>
            <p>
              Customers and visitors should not depend on analysis for making an investment; the analysis 
              should be considered as a <span className="text-gray-900 border-b border-indigo-500/50">first step</span> to 
              further their awareness of the subject. Visitors must consult a financial advisor before 
              making any serious investment-related decisions.
            </p>
          </DisclaimerSection>

          <DisclaimerSection title="Risk Acknowledgment">
            <p>
              You agree and understand that use of Option Bricks is at your own risk. Option Bricks is not 
              liable for any direct, indirect, incidental, consequential, or exemplary damages. 
            </p>
            <p>
              Failures in hardware, software, and Internet connections can delay the delivery of SMS, 
              which is not the responsibility of Option Bricks. SMS alerts are not to be forwarded 
              directly to any individual or firm; doing so may invite serious legal actions.
            </p>
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 text-sm italic text-gray-600">
              Any dispute will be solved in the territory of India, specifically at Thane, Navi Mumbai.
            </div>
          </DisclaimerSection>

          <DisclaimerSection title="Standard Disclosure/Disclaimer" isStandard={true}>
            <p>
              Option Bricks does not receive any consideration by way of remuneration or compensation 
              for any distribution or execution services in respect of the products or securities for 
              which the research service is provided.
            </p>
            <p className="text-gray-900 font-medium p-4 bg-indigo-500/10 rounded-xl border border-indigo-500/20">
              We do not provide any order execution services. If any person offers execution services 
              in the name of Option Bricks, please report them to us immediately.
            </p>
            <p>
              To ensure compliance with the <span className="text-indigo-700 font-bold">SEBI (Research Analysts) Regulations, 2014</span>,
              we have resolved that our research analysts and representatives will not make any trades 
              in the securities/instruments covered in our research services.
            </p>
          </DisclaimerSection>

          <DisclaimerSection title="Service Specifics">
            <p>
              All research services provided by us are communicated in <span className="text-gray-900 font-semibold underline decoration-indigo-500">written form</span>. 
              Any verbal communication by our executives or others shall not be considered as research services.
            </p>
            <p>
              Option Bricks provides a virtual chat room for conversations. However, the visitor is solely 
              responsible for their decisions in case of loss or benefit. We reserve the right to deny 
              the chat room service without explanation.
            </p>
          </DisclaimerSection>

          {/* Final Certification Box */}
          <div className="w-full p-10 rounded-[2.5rem] bg-gradient-to-br from-indigo-600/20 to-blue-600/20 border border-indigo-500/30 backdrop-blur-xl mt-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Independent SEBI Registration</h3>
            <p className="text-gray-700 text-lg leading-relaxed">
              Option Bricks is an independent SEBI (Securities and Exchange Board of India) registered 
              equity research firm operating under the license of its proprietor. Option Bricks or its 
              promoters, employees, directors, & stakeholders may have or may in the future have positions 
              in the stocks/instruments covered by us in our research reports.
            </p>
          </div>

        </div>

      </main>
    </div>
  );
}